import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, Chip, MenuItem, TextField, Typography} from '@mui/material';
import {toast} from 'react-toastify';
import {buildApi} from 'utils/api';

const statusLabels = {baseline: '기준표', measured: '실측', target: '대상 · 미실측', 'out-of-scope': '범위 밖'};
const statusColors = {baseline: 'info', measured: 'success', target: 'warning', 'out-of-scope': 'default'};

const checkText = check => typeof check === 'string' ? check : check?.text;
const rangeText = (input, range) => {
    if (input.kind === 'choice') return input.options.find(option => option.id === range)?.label || range;
    if (input.kind === 'jdk' && range.min === range.max) return String(range.min);
    return [range.min !== undefined && `${range.min} 이상`,
        range.max !== undefined && `${range.max}${input.kind === 'version' ? '.x까지' : ' 이하'}`].filter(Boolean).join(' · ');
};
const alternativeGuidance = (criteria, alternative) => {
    const conditions = criteria.inputs.filter(input => Object.hasOwn(alternative.when, input.id))
        .map(input => `${input.label} ${rangeText(input, alternative.when[input.id])}`).join(', ');
    const failures = alternative.failedInputs.map(failed => {
        const input = criteria.inputs.find(item => item.id === failed.id);
        return `${failed.label} ${rangeText(input, failed.required)} 필요`;
    }).join(', ');
    return `후보 ${alternative.ruleIndex + 1} (${conditions}): ${failures}`;
};

const versionChoices = (input, inputs) => input.choices?.[inputs[input.choicesBy]] || [];
const inputLabel = (input, inputs) => {
    const value = inputs[input.id];
    if (value === 'unlisted') return '목록에 없음';
    if (input.kind === 'choice') return input.options.find(option => option.id === value)?.label || value;
    if (input.kind === 'version' && input.choicesBy) {
        return versionChoices(input, inputs).find(option => String(option.value) === String(value))?.label || value;
    }
    return input.kind === 'jdk' ? `JDK ${value}` : value;
};

// 화면용 메모와 별도로 고객 안내를 구성한다. 내부 문구는 복사 대상에서 제외한다.
export const customerGuidance = (criteria, inputs, judgement) => [
    criteria.title,
    ...criteria.inputs.map(input => `${input.label}: ${inputLabel(input, inputs)}`),
    ...judgement.matches.flatMap(match => [
        statusLabels[match.status],
        ...match.results.flatMap(result => [
            `${result.label}${result.recommended ? ' (권장)' : ''}`,
            result.condition && `조건: ${result.condition}`,
            result.check && !result.check.internal && `확인: ${checkText(result.check)}`,
        ]),
        match.statusNote,
        ...match.notes.filter(note => !note.internal).map(note => note.text),
        match.evidence && `근거: ${match.evidence}`,
    ]),
    ...(judgement.alternatives || []).map(alternative => alternativeGuidance(criteria, alternative)),
    judgement.failedInputs.length > 0 && `범위 밖 항목: ${judgement.failedInputs.map(input => input.label).join(', ')}`,
].filter(Boolean).join('\n');

export default function InstallCriteria() {
    const [criteria, setCriteria] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [attempt, setAttempt] = useState(0);
    const [inputs, setInputs] = useState({});
    const [judgement, setJudgement] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const requestRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        setCriteria(null);
        setLoading(true);
        setLoadError('');
        setInputs({});
        setJudgement(null);
        setError('');
        setBusy(false);
        buildApi.getInstallCriteria()
            .then(({data}) => {
                if (!cancelled && data.available) setCriteria(data.criteria);
            })
            .catch(() => {
                if (!cancelled) setLoadError('설치 기준을 불러올 수 없습니다.');
            }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
            requestRef.current = null;
        };
    }, [attempt]);

    const changeInput = (id, value) => {
        requestRef.current = null;
        setInputs(previous => {
            const next = {...previous, [id]: value};
            for (const version of criteria.inputs.filter(input => input.kind === 'version' && input.choicesBy)) {
                if (id === version.choicesBy) {
                    next[version.id] = value && versionChoices(version, next).length === 0 ? 'unlisted' : '';
                }
                if (id === version.choicesBy || id === version.id) {
                    for (const jdk of criteria.inputs.filter(input => input.kind === 'jdk')) {
                        next[jdk.id] = next[version.id] === 'unlisted' ? 'unlisted' : '';
                    }
                }
            }
            return next;
        });
        setJudgement(null);
        setError('');
        setBusy(false);
    };

    const runJudge = async () => {
        const requestId = {};
        requestRef.current = requestId;
        setBusy(true);
        setJudgement(null);
        setError('');
        try {
            const {data} = await buildApi.judgeInstallCriteria(inputs);
            if (requestRef.current !== requestId) return;
            if (!data.available) setCriteria(null);
            else setJudgement(data);
        } catch (err) {
            if (requestRef.current === requestId) setError('설치 기준 판정에 실패했습니다. 다시 시도하세요.');
        } finally {
            if (requestRef.current === requestId) setBusy(false);
        }
    };

    const copyGuidance = async () => {
        try {
            await navigator.clipboard.writeText(customerGuidance(criteria, inputs, judgement));
            toast.success('고객 안내를 복사했습니다.');
        } catch (err) {
            toast.error('복사하지 못했습니다. 브라우저의 클립보드 권한을 확인하세요.');
        }
    };

    if (loading) return <Typography variant="body2">설치 기준을 불러오는 중...</Typography>;
    if (!criteria) return <Alert severity="info">
        공통 설치 기준을 불러올 수 없습니다.
        {loadError && <Button onClick={() => setAttempt(value => value + 1)}>다시 시도</Button>}
    </Alert>;

    return (
        <>
            <Box component="fieldset"
                 sx={{m: 0, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, minWidth: 0}}>
                <Typography component="legend" sx={{px: 0.5}}>{criteria.title}</Typography>
                <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 2, mt: 1}}>
                    {criteria.inputs.map(input => {
                        const version = criteria.inputs.find(item => item.kind === 'version' && item.choicesBy);
                        const dependent = input.kind === 'version' && input.choicesBy ? input
                            : input.kind === 'jdk' ? version : null;
                        const parentId = input.kind === 'version' ? dependent?.choicesBy : dependent?.id;
                        const parent = criteria.inputs.find(item => item.id === parentId);
                        const disabled = !!dependent && !inputs[parentId];
                        const selected = dependent && versionChoices(dependent, inputs)
                            .find(option => String(option.value) === String(inputs[dependent.id]));
                        const options = !dependent ? [] : input.kind === 'version'
                            ? versionChoices(input, inputs)
                            : (selected?.jdks || []).map(value => ({value, label: `JDK ${value}`}));
                        return (
                            <TextField
                                key={input.id}
                                label={input.label}
                                select={input.kind === 'choice' || !!dependent}
                                disabled={disabled}
                                multiline={input.kind === 'jdk' && !dependent}
                                maxRows={5}
                                size="small"
                                fullWidth
                                value={inputs[input.id] || ''}
                                onChange={event => changeInput(input.id, event.target.value)}
                                onKeyDown={event => {
                                    // 한 줄 입력에서는 Enter로 판정한다.
                                    if (event.key === 'Enter' && event.target.tagName === 'INPUT') {
                                        event.preventDefault();
                                        if (!busy && !event.nativeEvent.isComposing) runJudge();
                                    }
                                }}
                                helperText={disabled ? `${parent.label} 먼저 선택` : input.hint}
                                error={judgement?.outcome === 'invalid-input'
                                    && judgement.failedInputs.some(failed => failed.id === input.id)}
                            >
                                {dependent && [
                                    ...(disabled || options.length ? [<MenuItem key="" value="">
                                        {disabled ? `${parent.label} 먼저 선택` : '선택하세요'}
                                    </MenuItem>] : []),
                                    ...options.map(option => <MenuItem key={option.value}
                                                                       value={String(option.value)}>{option.label}</MenuItem>),
                                    <MenuItem key="unlisted" value="unlisted">목록에 없음</MenuItem>,
                                ]}
                                {input.kind === 'choice' && [
                                    <MenuItem key="" value="">선택하세요</MenuItem>,
                                    ...input.options.map(option => <MenuItem key={option.id}
                                                                             value={option.id}>{option.label}</MenuItem>),
                                ]}
                            </TextField>
                        );
                    })}
                </Box>
                <Button type="button" variant="outlined" onClick={runJudge} disabled={busy} sx={{mt: 2}}>
                    {busy ? '판정 중...' : '설치 기준 판정'}
                </Button>
                {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
                {judgement && (
                    <Box aria-live="polite" sx={{mt: 2}}>
                        {judgement.failedInputs.length > 0 && (
                            <Alert severity="warning" sx={{mb: 1}}>
                                {judgement.outcome === 'invalid-input' ? '입력을 확인하세요' : '범위 밖 항목'}
                                {`: ${judgement.failedInputs.map(input => input.label).join(', ')}`}
                            </Alert>
                        )}
                        {judgement.alternatives?.length > 0 && (
                            <Alert severity="warning" sx={{mb: 1}}>
                                가까운 설치 후보별 필요 조건 (각 후보는 별도 대안입니다):
                                {judgement.alternatives.map(alternative => (
                                    <Typography key={alternative.ruleIndex} variant="body2">
                                        {alternativeGuidance(criteria, alternative)}
                                    </Typography>
                                ))}
                            </Alert>
                        )}
                        {judgement.matches.map((match, index) => (
                            <Box key={index} sx={{mb: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                                <Chip label={statusLabels[match.status]} color={statusColors[match.status]} size="small"
                                      sx={{alignSelf: 'flex-start'}}/>
                                {match.results.map(result => (
                                    <Box key={result.id}>
                                        <Typography component="div" fontWeight={600}>
                                            {result.label} {result.recommended &&
                                            <Chip label="권장" color="primary" size="small"/>}
                                        </Typography>
                                        {result.condition &&
                                            <Typography
                                                sx={{whiteSpace: 'pre-wrap'}}>조건: {result.condition}</Typography>}
                                        {result.check && <Typography variant="body2" sx={{
                                            whiteSpace: 'pre-wrap',
                                            overflowWrap: 'anywhere'
                                        }}>
                                            {result.check.internal &&
                                                <Chip component="span" label="내부" size="small" sx={{mr: 0.5}}/>}
                                            확인: {checkText(result.check)}
                                        </Typography>}
                                    </Box>
                                ))}
                                {match.statusNote && <Alert
                                    severity={match.status === 'out-of-scope' ? 'warning' : 'info'}>{match.statusNote}</Alert>}
                                {match.notes.map((note, noteIndex) => (
                                    <Typography key={noteIndex} component="div" variant="body2"
                                                sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
                                        {note.internal && <Chip label="내부" size="small" sx={{mr: 0.5}}/>}{note.text}
                                    </Typography>
                                ))}
                                {match.evidence &&
                                    <Typography variant="caption"
                                                color="text.secondary">근거: {match.evidence}</Typography>}
                            </Box>
                        ))}
                        {judgement.outcome !== 'invalid-input' && (
                            <Button type="button" onClick={copyGuidance}>고객 안내 복사</Button>
                        )}
                    </Box>
                )}
                <Typography variant="caption" color="text.secondary"
                            sx={{display: 'block', mt: 1}}>출처: {criteria.source}</Typography>
            </Box>
        </>
    );
}
